/**
 * Lightweight pull-to-refresh for mobile pages.
 * Scroll parent is usually `.tc-mob__content` — we detect nearest scroll parent.
 */
import { useRef, useState, type ReactNode, type TouchEvent } from 'react';

function scrollParent(el: HTMLElement | null): HTMLElement | null {
  let n: HTMLElement | null = el;
  while (n) {
    const s = getComputedStyle(n);
    if ((s.overflowY === 'auto' || s.overflowY === 'scroll') && n.scrollHeight > n.clientHeight) {
      return n;
    }
    n = n.parentElement;
  }
  return null;
}

export function PullToRefresh({
  onRefresh,
  children,
  disabled = false,
}: {
  onRefresh: () => Promise<unknown> | void;
  children: ReactNode;
  disabled?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const threshold = 64;

  const atTop = () => {
    const sp = scrollParent(rootRef.current);
    if (!sp) return (rootRef.current?.getBoundingClientRect().top ?? 0) >= 0;
    return sp.scrollTop <= 0;
  };

  const onTouchStart = (e: TouchEvent) => {
    if (disabled || busy) return;
    if (!atTop()) return;
    startY.current = e.touches[0].clientY;
  };

  const onTouchMove = (e: TouchEvent) => {
    if (disabled || busy || !startY.current) return;
    if (!atTop()) {
      setPull(0);
      return;
    }
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setPull(Math.min(dy * 0.45, 96));
  };

  const onTouchEnd = async () => {
    if (disabled || busy) {
      setPull(0);
      startY.current = 0;
      return;
    }
    if (pull >= threshold) {
      setBusy(true);
      setPull(48);
      try {
        await onRefresh();
        if (navigator.vibrate) navigator.vibrate(12);
      } finally {
        setBusy(false);
        setPull(0);
      }
    } else {
      setPull(0);
    }
    startY.current = 0;
  };

  return (
    <div
      ref={rootRef}
      className="tc-mob-ptr"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className={`tc-mob-ptr__indicator ${busy || pull > 8 ? 'tc-mob-ptr__indicator--show' : ''}`}
        style={{ height: pull || (busy ? 48 : 0) }}
      >
        <div className={`tc-mob-ptr__spinner ${busy ? 'tc-mob-ptr__spinner--spin' : ''}`}>
          {busy ? 'SYNC' : pull >= threshold ? 'RELEASE' : 'PULL'}
        </div>
      </div>
      <div
        className="tc-mob-ptr__body"
        style={{ transform: pull ? `translateY(${Math.min(pull, 48)}px)` : undefined }}
      >
        {children}
      </div>
    </div>
  );
}
